/**
 * RTWO Indexer Engine — Adapter Registries
 */

import type {
  EventBusAdapterStatus,
  IEventBusAdapter,
  IEventBusAdapterRegistry,
} from '../adapters/event-bus-adapter-interface';
import { NullEventBusAdapter } from '../adapters/null-event-bus-adapter';
import type {
  ILedgerAdapter,
  ILedgerAdapterRegistry,
  LedgerAdapterStatus,
} from '../adapters/ledger-adapter-interface';
import { NullLedgerAdapter } from '../adapters/null-ledger-adapter';
import type {
  IProtocolAdapter,
  IProtocolAdapterRegistry,
  ProtocolAdapterStatus,
} from '../adapters/protocol-adapter-interface';
import { NullProtocolAdapter } from '../adapters/null-protocol-adapter';
import type {
  IStorageAdapter,
  IStorageAdapterRegistry,
  StorageAdapterStatus,
} from '../adapters/storage-adapter-interface';
import { NullStorageAdapter } from '../adapters/null-storage-adapter';

export class ProtocolAdapterRegistry implements IProtocolAdapterRegistry {
  private activeAdapter: IProtocolAdapter = new NullProtocolAdapter();
  private readonly adapters = new Map<string, IProtocolAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IProtocolAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-protocol-adapter') {
      return false;
    }
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullProtocolAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IProtocolAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): ProtocolAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class LedgerAdapterRegistry implements ILedgerAdapterRegistry {
  private activeAdapter: ILedgerAdapter = new NullLedgerAdapter();
  private readonly adapters = new Map<string, ILedgerAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: ILedgerAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-ledger-adapter') {
      return false;
    }
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullLedgerAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): ILedgerAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): LedgerAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class EventBusAdapterRegistry implements IEventBusAdapterRegistry {
  private activeAdapter: IEventBusAdapter = new NullEventBusAdapter();
  private readonly adapters = new Map<string, IEventBusAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IEventBusAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-event-bus-adapter') {
      return false;
    }
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullEventBusAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IEventBusAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): EventBusAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class StorageAdapterRegistry implements IStorageAdapterRegistry {
  private activeAdapter: IStorageAdapter = new NullStorageAdapter();
  private readonly adapters = new Map<string, IStorageAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IStorageAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-storage-adapter') {
      return false;
    }
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullStorageAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IStorageAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): StorageAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export const protocolAdapterRegistry = new ProtocolAdapterRegistry();
export const ledgerAdapterRegistry = new LedgerAdapterRegistry();
export const eventBusAdapterRegistry = new EventBusAdapterRegistry();
export const storageAdapterRegistry = new StorageAdapterRegistry();
